/**
 * Redaktionella block i kunskapsartiklar.
 *
 * Idén: AI:n skriver löptexten, men allt som ska vara *vårt eget* — prisjämförelser
 * ur lagret, interna nyckeltal, kalkylatorer, egna bilder och Fyndanu-tips — byggs
 * av kod med riktiga data. Då kan inget hallucineras och priserna är alltid färska,
 * även i artiklar som skrevs för ett halvår sedan.
 *
 * Placeringen är deterministisk (hash på slug) — samma artikel får alltid samma
 * block på samma ställe, men olika artiklar får olika mix. Ingen Math.random:
 * det skulle ge hydration-mismatch och nya block vid varje render.
 */

export type KbBlockKind =
  | "price-compare"
  | "fleet-stats"
  | "calc-tco"
  | "calc-monthly"
  | "calc-payload"
  | "photo-note"
  | "fyndanu-tip";

export type KbBlock = {
  kind: KbBlockKind;
  /** Antal H2-avsnitt in i artikeln som blocket placeras efter. */
  afterSection: number;
};

/** Stabil 32-bitars hash (FNV-1a) — samma på server och klient. */
export function hashString(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

type Rule = {
  kind: KbBlockKind;
  /** Ämnesmatchning mot sökord + rubriker. */
  test: RegExp;
  /** Högre vikt = väljs först när flera passar. */
  weight: number;
};

/**
 * Block matchas mot ämnet — aldrig påklistrat. En artikel om gasol får ingen
 * TCO-kalkylator, en artikel om att köpa husbil får ingen dieselvärmartabell.
 */
const RULES: Rule[] = [
  {
    kind: "price-compare",
    test: /pris|kosta|billig|dyr|k[öo]pa|s[äa]lja|v[äa]rde|budget|beg?agnad|marknad|f[öo]rmedling/i,
    weight: 100,
  },
  {
    kind: "calc-tco",
    test: /kosta|kostnad|driftkostnad|[äa]ga|totalkostnad|ekonomi|v[äa]rdeminskning|avskrivning|f[öo]rs[äa]kring|skatt/i,
    weight: 95,
  },
  {
    kind: "calc-monthly",
    test: /l[åa]n|finansiering|r[äa]nta|avbetalning|m[åa]nadskostnad|leasing|kontantinsats/i,
    weight: 92,
  },
  {
    kind: "calc-payload",
    test: /vikt|lastvikt|totalvikt|tj[äa]nstevikt|k[öo]rkort|b96|last|[öo]verlast|dragvikt/i,
    weight: 90,
  },
  {
    kind: "fleet-stats",
    test: /.../,
    weight: 60,
  },
  {
    kind: "photo-note",
    test: /fukt|skada|besiktning|service|kontroll|inspektion|underrede|tak|list|vinter|f[öo]rvaring|d[äa]ck/i,
    weight: 70,
  },
  {
    kind: "fyndanu-tip",
    test: /k[öo]pa|s[äa]lja|annons|beg?agnad|marknad|hitta|leta|bedrager|bedr[äa]ger|handlare|privat/i,
    weight: 80,
  },
];

/**
 * Väljer block för en artikel. Deterministiskt, max 3 block, aldrig två i rad
 * och aldrig i första eller sista avsnittet (där texten ska stå för sig själv).
 */
export function planKbBlocks(slug: string, keyword: string, content: string): KbBlock[] {
  const headings = [...content.matchAll(/^##\s+(.+)$/gm)].map((m) => m[1]);
  const sectionCount = headings.length;
  if (sectionCount < 4) return [];

  const haystack = `${keyword} ${headings.join(" ")}`;
  const seed = hashString(slug);

  const eligible = RULES.filter((r) => r.test.test(haystack))
    // Liten deterministisk jitter så att artiklar med samma ämne inte får identisk mix.
    .map((r, i) => ({ ...r, score: r.weight + (((seed >> (i * 3)) & 7) * 3) }))
    .sort((a, b) => b.score - a.score);

  if (!eligible.length) return [];

  const maxBlocks = 2 + (seed % 2); // 2 eller 3
  const kinds = eligible.slice(0, maxBlocks);

  // Möjliga positioner: efter avsnitt 2 .. sectionCount-1, aldrig intilliggande.
  const slots: number[] = [];
  for (let s = 2; s <= sectionCount - 1; s++) slots.push(s);
  if (!slots.length) return [];

  const blocks: KbBlock[] = [];
  let cursor = (seed >> 5) % Math.max(1, Math.min(2, slots.length));
  for (const k of kinds) {
    if (cursor >= slots.length) break;
    blocks.push({ kind: k.kind, afterSection: slots[cursor] });
    cursor += 2; // aldrig två block i rad
  }
  return blocks;
}

/**
 * Delar markdown i segment vid H2-gränser så blocken kan skjutas in mellan
 * avsnitt — aldrig inuti en tabell, lista eller kodblock.
 */
export function splitByH2(content: string): string[] {
  const lines = content.split("\n");
  const segments: string[] = [];
  let current: string[] = [];
  let inFence = false;

  for (const line of lines) {
    if (/^```/.test(line)) inFence = !inFence;
    if (!inFence && /^##\s+\S/.test(line) && current.length) {
      segments.push(current.join("\n"));
      current = [line];
      continue;
    }
    current.push(line);
  }
  if (current.length) segments.push(current.join("\n"));
  return segments;
}
